import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { SupplierPayment, Supplier } from '../types'
import { formatDate } from '../utils/dateFormat'
import ConfirmDialog from '../components/ConfirmDialog'
import AccountTypeSelector from '../components/AccountTypeSelector'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const SupplierPayments: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [payments, setPayments] = useState<SupplierPayment[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  })
  const [editingPayment, setEditingPayment] = useState<SupplierPayment | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [paymentToDelete, setPaymentToDelete] = useState<SupplierPayment | null>(null)

  // Форма
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    supplierId: '',
    amount: '',
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    comment: '',
    accountId: ''
  })

  // Загружаем сохраненные фильтры из localStorage
  useEffect(() => {
    const savedDateFrom = localStorage.getItem('supplierPayments_dateFrom')
    const savedDateTo = localStorage.getItem('supplierPayments_dateTo')
    
    if (savedDateFrom) {
      setDateFrom(savedDateFrom)
    }
    if (savedDateTo) {
      setDateTo(savedDateTo)
    }
  }, [])

  useEffect(() => {
    fetchSuppliers()
    fetchAccounts()
  }, [])

  // Загружаем данные после установки дат из localStorage
  useEffect(() => {
    // Небольшая задержка, чтобы даты успели установиться
    const timer = setTimeout(() => {
      fetchPayments()
    }, 100)
    
    return () => clearTimeout(timer)
  }, [dateFrom, dateTo])

  // Автоматическое обновление при изменении фильтров (кроме дат)
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 })) // Сбрасываем на первую страницу
    fetchPayments(1)
  }, [searchTerm, supplierFilter, statusFilter])

  // Обработка параметров "создать на основании"
  useEffect(() => {
    const createFrom = searchParams.get('createFrom')
    const purchaseType = searchParams.get('purchaseType') // 'inventory' или 'service'
    const supplierId = searchParams.get('supplierId')
    const amount = searchParams.get('amount')
    const description = searchParams.get('description')
    const date = searchParams.get('date')

    if (createFrom) {
      // Загружаем данные закупки по ID
      fetchPurchaseData(createFrom, purchaseType || 'inventory')
    } else if (supplierId || amount || description || date) {
      setFormData(prev => ({
        ...prev,
        ...(supplierId && { supplierId }),
        ...(amount && { amount }),
        ...(description && { comment: description }),
        ...(date && { date })
      }))
      setShowForm(true)
    }
  }, [searchParams])

  // Функция для загрузки данных закупки
  const fetchPurchaseData = async (purchaseId: string, purchaseType: string = 'inventory') => {
    try {
      const apiUrl = purchaseType === 'service' ? `/service-purchases/${purchaseId}` : `/purchases/${purchaseId}`
      const purchase = await apiGet<any>(apiUrl)
        const readableDate = formatDate(purchase.date)
        const documentNumber = purchaseType === 'service' 
          ? purchase.number
          : purchase.number
        setFormData(prev => ({
          ...prev,
          supplierId: purchase.supplierId || purchase.supplier?.id,
          amount: purchase.totalAmount.toString(),
          comment: purchaseType === 'service' 
            ? `Оплата по закупке услуг ${documentNumber} от ${readableDate}` 
            : `Оплата по закупке ${purchase.number}`,
          date: purchase.date.split('T')[0]
        }))
        setShowForm(true)
    } catch (error) {
      console.error('Ошибка загрузки данных закупки:', error)
    }
  }

  const fetchPayments = async (page = 1) => {
    try {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (supplierFilter) params.append('supplierId', supplierFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (dateFrom) params.append('from', dateFrom)
      if (dateTo) params.append('to', dateTo)
      params.append('page', page.toString())
      params.append('limit', '10')

      const data = await apiGet<{ payments?: SupplierPayment[]; pagination?: any }>(`/supplier-payments?${params}`)
      setPayments(data.payments || [])
      setPagination(data.pagination || { page: 1, limit: 10, total: 0, pages: 0 })
    } catch (error) {
      console.error('Error fetching payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSuppliers = async () => {
    try {
      // Загружаем всех поставщиков (с клубами и без)
      const data = await apiGet<{ suppliers?: Supplier[] }>('/suppliers?isActive=true&limit=1000')
      setSuppliers(data.suppliers || [])
    } catch (error) {
      console.error('Error fetching suppliers:', error)
    }
  }

  const fetchAccounts = async () => {
    try {
      const data = await apiGet<any[]>('/cash-accounts')
      setAccounts(data)
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }))
    fetchPayments(newPage)
  }

  // Функции для работы с фильтрами по датам
  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    localStorage.setItem('supplierPayments_dateFrom', value)
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    localStorage.setItem('supplierPayments_dateTo', value)
  }

  const handleResetFilters = () => {
    setDateFrom('')
    setDateTo('')
    localStorage.removeItem('supplierPayments_dateFrom')
    localStorage.removeItem('supplierPayments_dateTo')
  }

  const handleRefresh = () => {
    fetchPayments()
  }

  const handleCreate = () => {
    setEditingPayment(null)
    setFormData({
      date: new Date().toISOString().split('T')[0],
      supplierId: '',
      amount: '',
      method: 'CASH',
      status: 'DRAFT',
      comment: '',
      accountId: ''
    })
    setShowForm(true)
  }

  const handleNavigateToPurchases = () => {
    navigate('/purchases')
  }

  const handleNavigateToServicePurchases = () => {
    navigate('/service-purchases')
  }

  const handleEdit = (payment: SupplierPayment) => {
    setEditingPayment(payment)
    setFormData({
      date: payment.date.split('T')[0],
      supplierId: payment.supplierId,
      amount: payment.amount.toString(),
      method: payment.method,
      status: payment.status,
      comment: payment.comment || '',
      accountId: payment.accountId || ''
    })
    setShowForm(true)
  }

  const handleFormSave = async () => {
    try {
      // Автоматически определяем способ оплаты на основе выбранного счета
      const selectedAccount = accounts.find(account => account.id === formData.accountId);
      const paymentMethod = selectedAccount ? selectedAccount.type : 'CASH'; // Default to CASH if not found

      const payload = {
        ...formData,
        method: paymentMethod // Use the determined paymentMethod
      };

      if (editingPayment) {
        await apiPut(`/supplier-payments/${editingPayment.id}`, payload)
      } else {
        await apiPost('/supplier-payments', payload)
      }

      setShowForm(false)
      setEditingPayment(null)
      fetchPayments()
    } catch (error: any) {
      console.error('Error saving payment:', error)
      alert(error.message || 'Ошибка сохранения')
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingPayment(null)
  }

  const handleDeleteClick = (payment: SupplierPayment) => {
    setPaymentToDelete(payment)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!paymentToDelete) return

    try {
      await apiDelete(`/supplier-payments/${paymentToDelete.id}`)
      setShowDeleteConfirm(false)
      setPaymentToDelete(null)
      fetchPayments()
    } catch (error: any) {
      console.error('Error deleting payment:', error)
      alert(error.message || 'Ошибка удаления')
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
    setPaymentToDelete(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'POSTED': return 'bg-green-100 text-green-800'
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'POSTED': return 'Проведен'
      case 'DRAFT': return 'Черновик'
      case 'CANCELLED': return 'Отменен'
      default: return status
    }
  }


  const getAccountTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'CASH': 'Касса',
      'BANK': 'Банк',
      'CARD': 'Карта',
      'EXTERNAL': 'Внешний'
    };
    return typeLabels[type] || type;
  };

  const getAccountTypeColor = (type: string) => {
    const typeColors: { [key: string]: string } = {
      'CASH': 'bg-green-100 text-green-800',
      'BANK': 'bg-blue-100 text-blue-800',
      'CARD': 'bg-purple-100 text-purple-800',
      'EXTERNAL': 'bg-gray-100 text-gray-800'
    };
    return typeColors[type] || 'bg-gray-100 text-gray-800';
  };

  // Фильтрация теперь происходит на сервере

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Загрузка...</div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Оплаты поставщикам</h1>
          <div className="flex space-x-3">
            <button 
              onClick={handleNavigateToPurchases}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Закупка ТМЦ
            </button>
            <button 
              onClick={handleNavigateToServicePurchases}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              Закупка услуг
            </button>
            <button 
              onClick={handleCreate}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Создать оплату
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Поиск
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Номер, комментарий, поставщик"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Поставщик
              </label>
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все поставщики</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}{supplier.club ? ` 🏢 ${supplier.club.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Статус
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Все статусы</option>
                <option value="DRAFT">Черновик</option>
                <option value="POSTED">Проведен</option>
                <option value="CANCELLED">Отменен</option>
              </select>
            </div>
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">
                 Дата с
               </label>
               <input
                 type="date"
                 value={dateFrom}
                 onChange={(e) => handleDateFromChange(e.target.value)}
                 className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                 title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
               />
             </div>
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">
                 Дата по
               </label>
               <input
                 type="date"
                 value={dateTo}
                 onChange={(e) => handleDateToChange(e.target.value)}
                 className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                 title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
               />
             </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
              >
                Сбросить
              </button>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                Обновить
              </button>
            </div>
          </div>
        </div>

        {/* Payments List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Номер
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Поставщик
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Сумма
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Счет
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map(payment => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {payment.number || '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                    {formatDate(payment.date)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                    {payment.supplier?.name || '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                    {payment.amount.toLocaleString('ru-RU')} MDL
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                    {payment.account ? (
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-900 text-xs">{payment.account.name}</span>
                        <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded-full ${getAccountTypeColor(payment.account.type)}`}>
                          {getAccountTypeLabel(payment.account.type)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Не указан</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                      {getStatusText(payment.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium w-24">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        title="Редактировать"
                        onClick={() => handleEdit(payment)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-md transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9"/>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                        </svg>
                      </button>
                      <button
                        title="Удалить"
                        onClick={() => handleDeleteClick(payment)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-md transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/>
                          <path d="M14 11v6"/>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {payments.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Оплаты не найдены
            </div>
          )}

          {/* Пагинация */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total} записей
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Назад
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === pagination.page
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Вперед
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingPayment ? 'Редактировать оплату' : 'Создать оплату'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Поставщик *
                </label>
                <select
                  value={formData.supplierId}
                  onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Выберите поставщика</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}{supplier.club ? ` 🏢 ${supplier.club.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Сумма *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>


              <div>
                <AccountTypeSelector
                  value={formData.accountId}
                  onChange={(accountId) => setFormData({ ...formData, accountId })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Статус
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'DRAFT' | 'POSTED' | 'CANCELLED' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="DRAFT">Черновик</option>
                  <option value="POSTED">Проведен</option>
                  <option value="CANCELLED">Отменен</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Комментарий
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleFormCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleFormSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Удалить оплату"
          message={`Вы уверены, что хотите удалить оплату ${paymentToDelete?.number || 'без номера'}?`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="danger"
        />
      )}
    </>
  )
}

export default SupplierPayments
